import { AirClass, AirFareOption, RequestAirFare } from "@ssc-booking/api-contracts"
import * as F from "fp-ts/function"
import * as ROA from "fp-ts/ReadonlyArray"
import * as O from "fp-ts/Option"
import * as P from "fp-ts/Predicate"
import { match } from "ts-pattern"

export type AirClassOption = Omit<AirFareOption, "airFare"> & { airFare: AirClass }
type NoIncludedAirFare = {
    type: "none"
}
type SingleIncludedAirFare = {
    type: "single"
    base: AirClassOption
}
export type UpgradableIncludedAirFare = {
    type: "withUpgrade"
    base: AirClassOption
    upgrade: AirClassOption
}
export type FeasibleAirFares = NoIncludedAirFare | SingleIncludedAirFare | UpgradableIncludedAirFare

const hasUpgrade: Record<FeasibleAirFares["type"], boolean> = {
    single: false,
    withUpgrade: true,
    none: false,
}

const hasFlights: Record<FeasibleAirFares["type"], boolean> = {
    single: true,
    withUpgrade: true,
    none: false,
}

export const FeasibleAirFares = {
    hasUpgrade: (x: FeasibleAirFares): x is UpgradableIncludedAirFare => hasUpgrade[x.type],
    noIncluded: (): FeasibleAirFares => ({ type: "none" }),
    singleIncluded: (base: AirClassOption): FeasibleAirFares => ({ type: "single", base }),
    upgradable: (base: AirClassOption, upgrade: AirClassOption): FeasibleAirFares => ({
        type: "withUpgrade",
        base,
        upgrade,
    }),
    from: (reference: AirFareOption, allAirFareOptions: readonly AirFareOption[]) =>
        F.pipe(
            checkAirClassOption(reference),
            O.fold(() => FeasibleAirFares.noIncluded(), singleOrUpgradable(allAirFareOptions)),
        ),
    includes:
        (airFare: RequestAirFare) =>
        (x: FeasibleAirFares): boolean =>
            match(x)
                .with({ type: "none" }, () => airFare === "NotAvailable")
                .with({ type: "single" }, ({ base }) => airFare === base.airFare)
                .with(
                    { type: "withUpgrade" },
                    ({ base, upgrade }) => base.airFare === airFare || upgrade.airFare === airFare,
                )
                .exhaustive(),
    hasFlights: (x: FeasibleAirFares) => hasFlights[x.type],
}

const singleOrUpgradable = (allAirFareOptions: readonly AirFareOption[]) => (reference: AirClassOption) =>
    F.pipe(
        allAirFareOptions,
        ROA.findFirst(promotionalUpgradeFor(reference)),
        O.chain(checkAirClassOption),
        O.fold(
            () => FeasibleAirFares.singleIncluded(reference),
            (upgrade) => FeasibleAirFares.upgradable(reference, upgrade),
        ),
    )
const promotionalUpgradeFor = (fareOption: AirFareOption) =>
    F.pipe(AirFareOption.isUpgradeFor(fareOption), P.and(AirFareOption.isPromotional))

const checkAirClassOption = O.fromPredicate((x: AirFareOption): x is AirClassOption => x.airFare !== "CruiseOnly")
